import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';
import { useAuth } from '@opendoor/auth-fe/context';
import { getInitials } from 'components/landing-pages-v2/utils/getInitials';
import usePrevious from 'components/landing-pages-v2/utils/usePrevious';
import { useRouter } from 'next/router';

import { useObservability } from '../../../../helpers/observability';
import { COSMOS_URL } from '../../../globals';
import MenuPanel from '../menu/MenuPanel';
import type { MegaNavItem, NavigationProps } from '../types';
import { AccountSection } from './AccountSection';
import { LogoSection } from './LogoSection';
import { MegaNav } from './MegaNav';
import { SkipLink } from './SkipLink';
import { useLargerThanSM } from './useLargerThanSM';

interface HeaderProps extends NavigationProps {
  megaNavData?: MegaNavItem[];
  closeMenuRef?: MutableRefObject<() => void>;
}

const redirectToLogin = () => {
  document.location.href = `${COSMOS_URL}/login?redirectTo=${encodeURIComponent(
    window.location.pathname,
  )}`;
};

const Header = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaTitle,
  ctaLink,
  megaNavData: megaNavDataProp,
  closeMenuRef,
}: HeaderProps) => {
  const { pathname } = useRouter();
  const largerThanSM = useLargerThanSM();

  const { trackEvent } = useObservability();
  const { user: human, authentication } = useAuth();

  const isLoggedIn = authentication.state === 'authenticated';

  const initials = getInitials(human?.fullName, human?.firstName);
  const [isVisible, setIsVisible] = useState(false);
  const prevVisible = usePrevious(isVisible);
  const toggleMenuOpen = () => setIsVisible(!isVisible);

  const trackMenuClick = useCallback(() => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'menu', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  const trackAccountClick = useCallback(() => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  // Hang on to burger button ref for focus management
  const ctaRef = useRef<HTMLButtonElement>(null);
  // When menu close animation is complete
  // and it was previously open.
  // This prevents a focus from the initial animation state
  // on page load.
  const exitComplete = () => {
    if (prevVisible === true) {
      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
      if (!isInputFocused) {
        ctaRef.current?.focus();
      }
    }
  };

  //Hide Sign button on the sign in page
  const showLogin = !(pathname.includes('/login') || pathname.includes('/account'));

  useEffect(() => {
    if (closeMenuRef) {
      closeMenuRef.current = () => setIsVisible(false);
    }
  }, [closeMenuRef]);

  return (
    <>
      {!largerThanSM && (
        <MenuPanel
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaTitle={ctaTitle}
          ctaLink={ctaLink}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          exitComplete={exitComplete}
          megaNavData={megaNavDataProp}
        />
      )}

      <Box className="sticky top-0 z-[2000] block h-18 bg-od-bg-primary px-5 md:px-0">
        {/* Center the max-width grid via flex (atoms bans `mx-auto`). */}
        <Box className="flex h-18 items-center justify-center">
          <Grid className="h-18 w-full max-w-360 grid-cols-[auto_1fr_auto] md:px-10 lg:px-16">
            <SkipLink />
            <LogoSection />
            {/* 1 / -1 keeps things centered despite size of left and right columns */}
            <MegaNav slug={slug} mainNavLinks={mainNavLinks} megaNavData={megaNavDataProp} />
            <Row className="row-start-1 row-end-2 [grid-column:-1/-2] items-center gap-6 pl-4">
              <AccountSection
                isLoggedIn={isLoggedIn}
                showLogin={showLogin}
                initials={initials}
                redirectToLogin={redirectToLogin}
                trackAccountClick={trackAccountClick}
                trackMenuClick={trackMenuClick}
                onMobileMenuToggle={toggleMenuOpen}
                isMobileMenuVisible={isVisible}
                hamburgerRef={ctaRef}
              />
            </Row>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Header;
